import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '@/api/api';
import { BookOpen, Calendar, DollarSign, TrendingUp, Award, User } from 'lucide-react';

const StudentDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardAPI.getStudentDashboard();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching student dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-8 rounded-xl shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-4 rounded-full">
            <User size={48} />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Welcome, {data?.student_info?.name}!</h1>
            <p className="text-blue-100 mt-1">
              Class: {data?.student_info?.class} | Roll No: {data?.student_info?.roll_number}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={Calendar}
          title="Attendance"
          value={`${data?.attendance?.percentage}%`}
          subtitle={`${data?.attendance?.present_days} / ${data?.attendance?.total_days} days`}
          bgColor="bg-green-500"
        />
        <StatCard
          icon={DollarSign}
          title="Fee Balance"
          value={`₹${data?.fees?.balance?.toLocaleString('en-IN') || 0}`}
          subtitle={`Paid: ₹${data?.fees?.paid?.toLocaleString('en-IN') || 0}`}
          bgColor="bg-purple-500"
        />
        <StatCard
          icon={BookOpen}
          title="Total Fee"
          value={`₹${data?.fees?.total_fee?.toLocaleString('en-IN') || 0}`}
          subtitle={`Per year`}
          bgColor="bg-orange-500"
        />
      </div>

      {/* Recent Exams */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Award className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Recent Exam Results</h2>
        </div>
        
        {data?.recent_exams && data.recent_exams.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Exam Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Marks Obtained</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Total Marks</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Percentage</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Grade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.recent_exams.map((exam, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{exam.exam_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{exam.marks_obtained}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{exam.total_marks}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{exam.percentage}%</td>
                    <td className="px-4 py-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        exam.grade === 'A+' || exam.grade === 'A' ? 'bg-green-100 text-green-700' :
                        exam.grade === 'B' || exam.grade === 'B+' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {exam.grade}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No exam results available yet</p>
        )}
      </div>

      {/* Student Info Card */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Student Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Full Name" value={data?.student_info?.name} />
          <InfoItem label="Class" value={data?.student_info?.class} />
          <InfoItem label="Roll Number" value={data?.student_info?.roll_number} />
          <InfoItem label="Admission Number" value={data?.student_info?.admission_number} />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, subtitle, bgColor }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
    <div className="flex items-center justify-between mb-4">
      <div className={`${bgColor} p-3 rounded-full`}>
        <Icon className="text-white" size={24} />
      </div>
    </div>
    <p className="text-gray-600 text-sm font-medium">{title}</p>
    <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
  </div>
);

const InfoItem = ({ label, value }) => (
  <div className="border-b border-gray-100 pb-3">
    <p className="text-sm text-gray-600 mb-1">{label}</p>
    <p className="text-base font-semibold text-gray-800">{value || 'N/A'}</p>
  </div>
);

export default StudentDashboard;
